/**
 * Acciones COD archivos courier en la top bar (3PL): un desplegable para subir/enviar
 * y un botón aparte para ejecutar el job de bandeja (como InvoicesTopBarActions).
 */

import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Tooltip } from '@shared/components/ui/tooltip';
import { ChevronDown, Inbox, Layers, Upload } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

export interface CourierFilesTopBarActionsProps {
  onOpenUpload: () => void;
  onOpenBulkInbox: () => void;
  onOpenRunInbox: () => void;
  canRunInbox: boolean;
}

export const CourierFilesTopBarActions: FC<CourierFilesTopBarActionsProps> = ({
  onOpenUpload,
  onOpenBulkInbox,
  onOpenRunInbox,
  canRunInbox,
}) => {
  const { t } = useTranslation('codCourierFiles');

  const runDisabled = !canRunInbox;
  const runTooltip = t('inbox_run.error.no_team', 'No se pudo determinar el equipo actual.');

  const runButton = (
    <Button
      type="button"
      variant="outline"
      className="gap-1.5 border-card-border bg-card-bg"
      disabled={runDisabled}
      onClick={onOpenRunInbox}
      data-testid="header-courier-files-run-inbox"
    >
      <Inbox className="size-3.5 text-muted-foreground" aria-hidden />
      <span className="hidden sm:inline">{t('inbox_run.button', 'Procesar bandeja')}</span>
      <span className="sm:hidden">{t('inbox_run.button_short', 'Bandeja')}</span>
    </Button>
  );

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="primary" className="gap-1.5 pr-2" data-testid="header-courier-files-add-menu">
            <Upload className="size-3.5" aria-hidden />
            <span className="hidden sm:inline">{t('topbar.add_files', 'Añadir archivos')}</span>
            <span className="sm:hidden">{t('topbar.add_files_short', 'Añadir')}</span>
            <ChevronDown className="size-4 opacity-80" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[14rem] border-card-border bg-card-bg">
          <DropdownMenuItem
            onClick={onOpenUpload}
            className="cursor-pointer gap-2"
            data-testid="header-action-courier-upload-file"
          >
            <Upload className="size-3.5 text-muted-foreground" aria-hidden />
            {t('upload.button', 'Subir Archivo')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onOpenBulkInbox}
            className="cursor-pointer gap-2"
            data-testid="header-action-courier-bulk-inbox"
          >
            <Layers className="size-3.5 text-muted-foreground" aria-hidden />
            {t('bulk.button', 'Carga masiva')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {canRunInbox ? (
        runButton
      ) : (
        <Tooltip
          trigger={<span className="inline-flex">{runButton}</span>}
          content={runTooltip}
          data-testid="tooltip-courier-files-run-inbox"
        />
      )}
    </div>
  );
};
